/**
 * Alle Anlagen der Plattform — für den Admin (HM-161).
 *
 * **Stammdaten, keine Werte:** Name, Art, Status, Account, Besitzer und mit wem
 * eine Anlage geteilt ist. Wer eine Zeile öffnet, sieht die Teilungen und
 * offenen Einladungen und kann die Stufe ändern, eine Teilung beenden oder eine
 * Einladung zurücknehmen. **Anlegen kann er keine** — einen Zugang vergibt nur
 * der Besitzer, sonst könnte sich ein Admin selbst an fremde Messwerte setzen
 * (ADR 0014, Nachtrag in ADR 0043). Der Satz darüber sagt das, damit niemand
 * den fehlenden Knopf sucht.
 *
 * Die Zeilen einer Teilung sind dieselben wie in den Einstellungen der Anlage
 * (`ShareRow`, `PersonCell`): zwei Fassungen derselben Zeile liefen auseinander.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Badge, Button, Card, Input } from "@hausmeter/ui";
import { formatInstant } from "@hausmeter/i18n";
import type { ApiComponents } from "@hausmeter/sdk";
import { api } from "../lib/client.js";
import { apiErrorMessage } from "../lib/api-error.js";
import { displayName } from "../lib/format.js";
import { listRow, mutedText, plainList, rowLabel } from "../lib/list-styles.js";
import { endedInvitation, shareAccessLabel } from "../lib/site-access.js";
import { SITE_KIND_LABELS, siteKindIcon } from "../lib/site-kind.js";
import { PersonCell, ShareRow } from "./site-shares.js";

type AdminSite = ApiComponents["schemas"]["AdminSite"];
type Access = ApiComponents["schemas"]["SiteShareAccess"];

const STATUS_LABELS: Record<AdminSite["status"], string> = {
  draft: "Entwurf",
  active: "aktiv",
  archived: "archiviert",
};

export function AdminSitesCard() {
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);

  const sites = useQuery({
    queryKey: ["admin-sites", search],
    queryFn: async () => {
      const { data, error } = await api.GET("/admin/sites", {
        params: { query: { limit: 100, ...(search ? { search } : {}) } },
      });
      if (error || !data) throw new Error("Die Anlagen konnten nicht geladen werden.");
      return data;
    },
  });

  return (
    <Card
      title="Anlagen"
      subtitle="Wem jede Anlage gehört und mit wem sie geteilt ist. Ändern lassen sich Stufe und Teilung; neue Zugänge vergibt nur der Besitzer. Messwerte sind hier nicht einsehbar."
    >
      <Input
        label="Suche"
        value={search}
        placeholder="Name der Anlage oder E-Mail-Adresse des Besitzers"
        onChange={(event) => setSearch(event.target.value)}
      />

      {sites.isPending && <p style={{ ...mutedText, marginTop: "var(--sp-3)" }}>Wird geladen …</p>}
      {sites.isError && <p style={{ ...mutedText, marginTop: "var(--sp-3)" }}>{sites.error.message}</p>}
      {sites.data?.items.length === 0 && <p style={{ ...mutedText, marginTop: "var(--sp-3)" }}>Kein Treffer.</p>}

      {sites.data !== undefined && sites.data.items.length > 0 && (
        <ul style={{ ...plainList, marginTop: "var(--sp-3)" }}>
          {sites.data.items.map((site) => (
            <SiteRow
              key={site.id}
              site={site}
              open={openId === site.id}
              onToggle={() => setOpenId((current) => (current === site.id ? null : site.id))}
            />
          ))}
        </ul>
      )}
      {sites.data?.nextCursor !== null && sites.data !== undefined && (
        <p style={{ ...mutedText, marginTop: "var(--sp-3)" }}>Es gibt weitere Anlagen — grenz die Suche ein.</p>
      )}
    </Card>
  );
}

function SiteRow({ site, open, onToggle }: { readonly site: AdminSite; readonly open: boolean; readonly onToggle: () => void }) {
  const KindIcon = siteKindIcon(site.kind);
  const shared =
    site.shareCount === 0 ? "nicht geteilt" : site.shareCount === 1 ? "mit 1 Person geteilt" : `mit ${site.shareCount} Personen geteilt`;
  const panelId = `admin-site-${site.id}`;

  return (
    <li style={{ ...listRow, flexDirection: "column", alignItems: "stretch" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "var(--sp-3)", flexWrap: "wrap" }}>
        <KindIcon size={18} style={{ color: "var(--text-muted)", flexShrink: 0 }} aria-hidden />
        <span style={{ display: "flex", flexDirection: "column", minWidth: "min(220px, 100%)", flex: "1 1 220px" }}>
          <span style={{ ...rowLabel, overflowWrap: "anywhere" }}>{site.name}</span>
          <span style={{ ...mutedText, fontSize: 12, overflowWrap: "anywhere" }}>
            {SITE_KIND_LABELS[site.kind]} · {site.owner === null ? "ohne Besitzer" : displayName(site.owner)}
          </span>
        </span>
        <span style={mutedText}>
          {shared}
          {site.openInvitationCount > 0 ? ` · ${site.openInvitationCount} offen` : ""}
        </span>
        {site.status !== "active" && <Badge>{STATUS_LABELS[site.status]}</Badge>}
        <span style={{ marginLeft: "auto" }}>
          <Button variant="ghost" size="sm" aria-expanded={open} aria-controls={panelId} onClick={onToggle}>
            {open ? "Schließen" : "Berechtigungen"}
          </Button>
        </span>
      </div>
      {open && (
        <div id={panelId} style={{ marginTop: "var(--sp-3)" }}>
          <SiteSharesPanel siteId={site.id} />
        </div>
      )}
    </li>
  );
}

/** Besitzer, Teilungen, offene Einladungen — geladen erst beim Öffnen. */
function SiteSharesPanel({ siteId }: { readonly siteId: string }) {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const detail = useQuery({
    queryKey: ["admin-site-shares", siteId],
    queryFn: async () => {
      const { data, error: failed } = await api.GET("/admin/sites/{siteId}/shares", { params: { path: { siteId } } });
      if (failed || !data) throw new Error("Die Teilungen konnten nicht geladen werden.");
      return data;
    },
  });
  const refresh = async () => {
    setError(null);
    await client.invalidateQueries({ queryKey: ["admin-site-shares", siteId] });
    await client.invalidateQueries({ queryKey: ["admin-sites"] });
  };

  const change = useMutation({
    mutationFn: async (input: { shareId: string; access: Access }) => {
      const { error: failed } = await api.PATCH("/admin/site-shares/{shareId}", {
        params: { path: { shareId: input.shareId } },
        body: { access: input.access },
      });
      if (failed) throw new Error(apiErrorMessage(failed) ?? "Die Berechtigung ließ sich nicht ändern.");
    },
    onSuccess: refresh,
    onError: (cause: Error) => setError(cause.message),
  });
  const end = useMutation({
    mutationFn: async (shareId: string) => {
      const { error: failed } = await api.DELETE("/admin/site-shares/{shareId}", { params: { path: { shareId } } });
      if (failed) throw new Error(apiErrorMessage(failed) ?? "Die Teilung ließ sich nicht beenden.");
    },
    onSuccess: refresh,
    onError: (cause: Error) => setError(cause.message),
  });
  const revoke = useMutation({
    mutationFn: async (invitationId: string) => {
      const { error: failed } = await api.POST("/admin/invitations/{invitationId}/revoke", {
        params: { path: { invitationId } },
      });
      if (failed) throw new Error(apiErrorMessage(failed) ?? "Die Einladung ließ sich nicht zurücknehmen.");
    },
    onSuccess: refresh,
    onError: (cause: Error) => setError(cause.message),
  });

  if (detail.isPending) return <p style={mutedText}>Wird geladen …</p>;
  if (detail.isError) return <p style={mutedText}>{detail.error.message}</p>;

  const data = detail.data;
  const row = { ...listRow, borderTop: "none", borderBottom: "1px solid var(--border-subtle)" } as const;
  return (
    <div style={{ padding: "var(--sp-3)", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)" }}>
      <ul style={plainList}>
        {data.site.owner !== null && (
          <li style={row}>
            <PersonCell person={data.site.owner} />
            <span style={{ ...mutedText, fontSize: 12 }}>Account „{data.site.accountName}"</span>
            <span style={{ marginLeft: "auto" }}>
              <Badge tone="brand">Besitzer</Badge>
            </span>
          </li>
        )}
        {data.shares.map((share) => (
          <ShareRow
            key={share.id}
            share={share}
            busy={change.isPending || end.isPending}
            onChange={(access) => change.mutate({ shareId: share.id, access })}
            onEnd={() => end.mutate(share.id)}
          />
        ))}
        {data.invitations.map((invitation) => {
          const ended = endedInvitation(invitation.status);
          return (
            <li key={invitation.id} style={row}>
              <span style={{ ...rowLabel, minWidth: "min(220px, 100%)", overflowWrap: "anywhere" }}>{invitation.email}</span>
              <span style={mutedText}>
                {ended !== null
                  ? `${ended.hint} · ${shareAccessLabel(invitation.access)}`
                  : `eingeladen · ${shareAccessLabel(invitation.access)} · gültig bis ${formatInstant(new Date(invitation.expiresAt), "Europe/Berlin", "de-DE", "date")}`}
              </span>
              {ended === null && (
                <span style={{ marginLeft: "auto" }}>
                  <Button variant="ghost" size="sm" disabled={revoke.isPending} onClick={() => revoke.mutate(invitation.id)}>
                    Zurücknehmen
                  </Button>
                </span>
              )}
            </li>
          );
        })}
      </ul>
      {data.shares.length === 0 && data.invitations.length === 0 && (
        <p style={{ ...mutedText, marginTop: "var(--sp-2)" }}>Mit niemandem geteilt.</p>
      )}
      {error !== null && (
        <div style={{ marginTop: "var(--sp-3)" }}>
          <Alert tone="danger">{error}</Alert>
        </div>
      )}
    </div>
  );
}
